import { cn } from "@/domains/library"

/**
 * Semantic variants distinguish the three APR numbers a user sees across the
 * Manual CL surfaces:
 *
 *   - `pool`      — the pool-wide "displayApr" (24h-fees ÷ TVL annualised).
 *                   Solid primary pill. Used on pool lists, pool headers,
 *                   metric rows.
 *   - `estimated` — prospective APR shown during create-position. Dashed
 *                   border with a `~` prefix so users never confuse a
 *                   projection for a result.
 *   - `realised`  — accrued APR on manage / my-positions pages.
 *                   Solid success-green pill — this has been earned or is claimable.
 */
export type AprBadgeVariant = "pool" | "estimated" | "realised"

export type AprBadgeProps = {
  /**
   * Pre-formatted APR string from the store — e.g. `"4.50%"`, `"121.67%"`,
   * or `"—"`. Empty string and literal `"—"` both render the neutral
   * placeholder so callers don't need to handle the undefined case
   * themselves.
   */
  value: string
  /**
   * Which semantic treatment to apply. Defaults to `pool` so existing
   * call-sites keep their current look without a prop change.
   */
  variant?: AprBadgeVariant
  /**
   * When true, forces the badge into its "Out of range" state regardless of
   * `value`: renders `0.00%` with a small adjacent tag so the user can tell
   * at a glance that the projection is not currently earning.
   */
  outOfRange?: boolean
  className?: string
  /** Slightly smaller variant used inside dense stat tiles. */
  size?: "sm" | "md"
}

/**
 * Small pill used to highlight APR values across the Manual CL surfaces.
 * Centralises the APR visual so every pool / position number renders with
 * the same shape, border, and colour treatment, while the `variant` prop
 * encodes the semantic difference between a pool-wide rate, a projection,
 * and a realised result.
 *
 * Neutral fallback (dash) is rendered as plain text so unknown APRs don't
 * get visually promoted.
 */
export function AprBadge({
  value,
  variant = "pool",
  outOfRange = false,
  className,
  size = "md",
}: AprBadgeProps) {
  const hasValue = Boolean(value) && value !== "—"
  if (!hasValue && !outOfRange) {
    return <span className={cn("text-t300", className)}>—</span>
  }

  const displayValue = outOfRange ? "0.00%" : prefixForVariant(variant, value)
  const sizeClasses = size === "sm" ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-0.5 text-xs"
  const variantClasses = classesForVariant(variant, outOfRange)

  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <span
        data-testid="apr-badge"
        data-variant={variant}
        data-out-of-range={outOfRange ? "true" : "false"}
        className={cn(
          "inline-flex items-center rounded-md border font-medium tracking-wide",
          sizeClasses,
          variantClasses,
        )}
      >
        {displayValue}
      </span>
      {outOfRange && (
        <span
          data-testid="apr-badge-out-of-range-tag"
          className="text-t300 text-[10px] uppercase tracking-wider"
        >
          Out of range
        </span>
      )}
    </span>
  )
}

function prefixForVariant(variant: AprBadgeVariant, value: string): string {
  if (variant !== "estimated") return value
  if (value.startsWith("~") || value.startsWith("≈")) return value
  return `~${value}`
}

function classesForVariant(variant: AprBadgeVariant, outOfRange: boolean): string {
  if (outOfRange) {
    return "border-p200-light bg-p300 text-t200"
  }
  switch (variant) {
    case "estimated":
      return "border-dashed border-primary-400/60 bg-primary-400/5 text-primary-400/90"
    case "realised":
      return "border-success-border bg-success-background/20 text-success-text"
    case "pool":
    default:
      return "border-primary-400/40 bg-primary-400/10 text-primary-400"
  }
}
